import {
  formatComplianceDocumentPlaceholderMessage,
  normalizePlaceholderGuidanceAttribute,
  NOT_FOUND_PLACEHOLDER,
} from '@artem-lex/compliance-documents'
import { useCallback, useContext, useMemo, useRef } from 'react'
import type { MarkViewProps } from '@tiptap/core'
import type { MarkViewRendererProps } from '@tiptap/react'
import { ReactMarkViewContext } from '@tiptap/react'
import { useComplianceDocumentManifestContextOptional } from '@/features/compliance-studio/machinery-directive/context/ComplianceDocumentManifestContext'
import { DismissibleComplianceMarkShellDismissButton } from '../dismissible-compliance-mark-shell'
import { removeMark } from '../remove-mark-from-mark-view'
import { NOT_SPECIFIED_MARK } from './not-specified-mark'

const FALLBACK_SENTINEL_DISPLAY = {
  notFound: 'Not found',
  notFoundWithField: 'Not found: {{field}}',
  notFoundWithFieldAndGuidance: 'Not found: {{field}} | {{guidance}}',
  dismissAriaLabel: 'Dismiss notice',
  insufficientInformation:
    'Insufficient information: No relevant content was found in the product sources for this section. Please add source documents or complete manually.',
}

export function NotSpecifiedMarkView(props: MarkViewRendererProps) {
  const placeholder = (props.mark?.attrs?.placeholder as string | null) ?? null
  const fieldLabel = (props.mark?.attrs?.fieldLabel as string | null) ?? null
  const rawGuidance = (props.mark?.attrs?.guidance as string | null) ?? null
  const guidance = useMemo(
    () => normalizePlaceholderGuidanceAttribute(rawGuidance),
    [rawGuidance]
  )
  const manifestContext = useComplianceDocumentManifestContextOptional()

  const message = useMemo(() => {
    if (manifestContext) {
      return manifestContext.formatPlaceholderMessage(
        'not_found',
        fieldLabel,
        guidance
      )
    }

    return formatComplianceDocumentPlaceholderMessage(
      'not_found',
      FALLBACK_SENTINEL_DISPLAY,
      fieldLabel,
      guidance
    )
  }, [fieldLabel, guidance, manifestContext])

  const dismissLabel = manifestContext
    ? manifestContext.getDismissAriaLabel()
    : 'Dismiss notice'

  const { markViewContentRef } = useContext(ReactMarkViewContext)

  const hostRef = useRef<HTMLSpanElement | null>(null)
  const setHostRef = useCallback(
    (element: HTMLSpanElement | null) => {
      hostRef.current = element
      markViewContentRef(element)
    },
    [markViewContentRef]
  )

  const handleDismiss = () => {
    const host = hostRef.current
    if (!host) {
      return
    }
    removeMark(NOT_SPECIFIED_MARK, props as MarkViewProps, host)
  }

  return (
    <span
      ref={setHostRef}
      contentEditable={false}
      className='inline-flex max-w-full cursor-default items-center gap-2 rounded border-l-4 border-yellow-500 bg-yellow-50 px-2 select-none *:last:pointer-events-none *:last:sr-only dark:bg-yellow-950'
    >
      <span
        className='text-sm leading-relaxed text-yellow-950 dark:text-yellow-100'
        data-not-specified=''
        data-placeholder={placeholder ?? NOT_FOUND_PLACEHOLDER}
        data-field-label={fieldLabel ?? undefined}
        data-guidance={guidance ?? undefined}
        title={message}
      >
        {message}
      </span>
      <DismissibleComplianceMarkShellDismissButton
        dismissLabel={dismissLabel}
        onDismiss={handleDismiss}
        variant='yellow'
      />
    </span>
  )
}
